import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { A11y, Controller, EffectFade, Mousewheel, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import cls from 'classnames';

import { getContent, isDDEnv } from '@/utils/helper';
import { useIsKorean } from '@/utils/useLocale';
import {
  analytics,
  getCarouselDotNext,
  getClickCtaDetail,
  getClickExternalCtaDetail,
} from '@/utils/analytics';

import Pill from '@/atoms/Pill/Pill';
import Text from '@/atoms/Text/Text';
import { DynamicAsset } from '@/molecules/Media/index';
import CtfCta, { Cta } from '@/molecules/CTA/index';
import RichText from '@/molecules/RichText/RichText';

import {
  CtfAssetPositions,
  CtfCarouselStyle,
  CtfCtaStyle,
} from '@/utils/constants';
import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';

const SWIPER_MODULES = [A11y, Controller, Mousewheel, Pagination];

const SWIPER_PAGINATION = {
  clickable: true,
  el: '.aaco-carousel-featured__pagination',
  type: 'bullets',
};

const SWIPER_SPEED = 1000;

function CarouselFeatured({ assets = [], entries = [], fields = null, carouselContentStyle = {
    compactSpace: false,
    hasBackground: false,
    linkStyle: '',
  } }) {
  const isKorean = useIsKorean();
  const paginationRef = useRef(null);

  const [firstSwiper, setFirstSwiper] = useState(null);
  const [secondSwiper, setSecondSwiper] = useState(null);

  const {
    assetPosition,
    backgroundColor,
    cards,
    title,
    carouselStyle,
    removeYPadding,
    id,
  } = fields;

  const backgrounds = {
    transparent: 'bg-transparent',
    'progressive blue': 'bg-progressive-blue',
    'natural cream': 'bg-natural-cream',
  };
  const isLightBackground = backgroundColor === 'transparent';
  const isDarkBackground = backgroundColor === 'progressive blue';
  const isNeutralBackground = backgroundColor === 'natural cream';
  const bgColor = backgroundColor
    ? backgrounds[backgroundColor.toLowerCase()]
    : null;

  const getCtaAnalytics = (e, data) => {
    analytics({
      window,
      document,
      detail: data?.localUrl
        ? getClickCtaDetail(e, data)
        : getClickExternalCtaDetail(e, data),
    });
  };

  useEffect(() => {
    if (paginationRef?.current) {
      paginationRef.current
        ?.querySelectorAll('.swiper-pagination-bullet')
        .forEach(function (bullet) {
          bullet.addEventListener('click', function (el) {
            analytics({
              window,
              document,
              detail: getCarouselDotNext(el),
            });
          });
        });
    }
  }, [paginationRef]);

  const isDD = isDDEnv();
  const isWH = !isDD;

  return (
    <div
      id={id}
      className={`aaco-carousel-featured overflow-hidden
      ${isDarkBackground ? 'aaco-carousel-featured--has-dark-bg' : ''} ${
        isLightBackground ? 'aaco-carousel-featured--has-light-bg' : ''
      } ${isNeutralBackground ? 'aaco-carousel-featured--has-neutral-bg' : ''}
      ${
        carouselStyle === CtfCarouselStyle.FEATURED_FULL
          ? 'aaco-carousel-featured--full-width'
          : 'aaco-carousel-featured--full-padding'
      } ${
        assetPosition === CtfAssetPositions.LEFT
          ? 'aaco-carousel-featured--left-image'
          : 'aaco-carousel-featured--right-image'
      } ${!carouselContentStyle.compactSpace && !removeYPadding && 'pb-6 md:pb-20'}`}
      aria-label={title}
    >
      <div
        className={cls({
          'container mx-auto': carouselStyle !== CtfCarouselStyle.FEATURED_FULL,
          'container mx-auto': isWH || isDD,
        })}
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div
            className={`aaco-carousel__swiper ${
              assetPosition === CtfAssetPositions.LEFT
                ? 'md:dd-kr:order-first md:dd-en:order-first'
                : 'md:dd-kr:order-1 md:dd-en:order-1'
            }`}
          >
            <Swiper
              controller={{ control: secondSwiper }}
              direction="horizontal"
              modules={SWIPER_MODULES}
              mousewheel={{
                forceToAxis: true,
              }}
              onSwiper={setFirstSwiper}
              speed={SWIPER_SPEED}
            >
              {cards &&
                cards.map((card, index) => {
                  const content = getContent(card.sys?.id, entries);
                  const { dynamicAsset, cloudinaryAssets, title } =
                    content?.fields || {};
                  return (
                    <SwiperSlide key={index}>
                      <div className="aaco-carousel-card__slide">
                        {(cloudinaryAssets || dynamicAsset) && (
                          <div className="aaco-card__image">
                            <DynamicAsset
                              assets={assets}
                              entries={entries}
                              field={cloudinaryAssets ?? dynamicAsset}
                              style={`${
                                carouselStyle === CtfCarouselStyle.FEATURED_FULL
                                  ? ''
                                  : 'dd-en:rounded-t-[8px] dd-kr:rounded-t-[8px] dd-en:overflow-hidden dd-kr:overflow-hidden dd-en:md:rounded-none dd-kr:md:rounded-none'
                              } ${
                                carouselStyle === CtfCarouselStyle.FEATURED &&
                                isLightBackground
                                  ? 'dd-en:rounded-b-[8px] dd-kr:rounded-b-[8px]'
                                  : ''
                              }`}
                              container={
                                carouselStyle === CtfCarouselStyle.FEATURED_FULL
                                  ? 'half'
                                  : 'half-container'
                              }
                              title={title}
                            />
                          </div>
                        )}
                      </div>
                    </SwiperSlide>
                  );
                })}
            </Swiper>
          </div>
          <div
            className={cls(
              'aaco-carousel__content',
              'flex flex-col items-center',
              {
                'justify-center': isDD,
                'justify-between': isWH,
              },
              'min-h-full overflow-hidden',
              bgColor,
              {
                'text-white': !isDarkBackground,
                'text-progressive-blue': isDD && !isDarkBackground,

                'md:px-[60px] md:py-[33px] md:pt-0 md:pr-0': isWH,
                'pt-[25px] pb-[29px] md:py-[89px] px-[30px] md:px-[100px]':
                  isDD && carouselStyle === CtfCarouselStyle.FEATURED_FULL,
                'rounded-b-[8px] md:rounded-none md:py-[88.5px] md:px-[100px]':
                  isDD && carouselStyle !== CtfCarouselStyle.FEATURED_FULL,
                'p-0 pt-[25px]': isDD && isLightBackground,
                'p-[30px]': isDD && !isLightBackground,
              }
            )}
          >
            <Swiper
              className="w-full min-h-full flex flex-col justify-between flex-1"
              controller={{ control: firstSwiper }}
              effect="fade"
              fadeEffect={{ crossFade: true }}
              modules={[...SWIPER_MODULES, EffectFade]}
              onSwiper={setSecondSwiper}
              pagination={SWIPER_PAGINATION}
              speed={SWIPER_SPEED}
            >
              {cards &&
                cards.map((card, index) => {
                  const content = getContent(card.sys?.id, entries);
                  let {
                    primaryCTA,
                    title,
                    text,
                    tags = [],
                    subtitle,
                  } = content?.fields || {};
                  text =
                    text && text.sys
                      ? getContent(text.sys.id, entries)?.fields?.text
                      : text;

                  const tag =
                    tags.length > 0
                      ? getContent(tags[0].sys?.id, entries)
                      : null;

                  if (!content) return null;
                  return (
                    <SwiperSlide key={index}>
                      <div className="content relative min-h-full flex flex-col items-start justify-center">
                        {(tag && !isWH) && (
                          <Pill
                            invert={true}
                            style={`bg-acacia mb-0 inline-flex items-center`}
                            onClick={() =>
                              {/* Search disabled */}
                            }
                            small
                          >
                            {tag.fields?.name}
                          </Pill>
                        )}
                        {subtitle && (
                          <Text
                            tag="tag"
                            style={cls(
                              'text-dark-warm-grey',
                              {
                                'text-white': isDD && isDarkBackground,
                                'text-grey-6': isDD && !isDarkBackground,
                              },
                              {
                                'py-0': isDD,
                                'py-[22px]': isWH,
                                'pt-[25px]': isDD && tag,
                              }
                            )}
                          >
                            {subtitle}
                          </Text>
                        )}
                        <div
                          className={cls(
                            'flex grow flex-col justify-center items-start',
                            {
                              'gap-[22px] mb-4 md:justify-between': isWH,
                              'pt-5 md:pt-0': !subtitle && isWH,
                              'mb-4': isDD,
                            }
                          )}
                        >
                          <h2
                            className={cls(
                              'aaco-carousel__content__title',
                              'font-h4-mobile text-h4-mobile md:font-h4 md:text-h4',
                              'font-normal font-heading',
                              {
                                'mb-0 pt-5': isDD,
                                'text-black md:border-b-2 md:pb-5 border-black': isWH,
                                'text-progressive-blue': isDD,
                              }
                            )}
                          >
                            {title}
                          </h2>
                          {text && !text.content && (
                            <p
                              className={cls(
                                'font-text text-body',
                                'whitespace-pre-line',
                                {
                                  'text-black': isWH,
                                  'text-80-white-on-dark-blue': isDD,

                                  'text-body-text-grey':
                                    isDD && isLightBackground,
                                  'opacity-50 dd-kr:opacity-100 dd-en:opacity-100':
                                    !isLightBackground,
                                },
                                {
                                  'mt-[15px]': isDD,
                                  'pl-[118px] md:pl-[111px]': isWH,
                                }
                              )}
                            >
                              {text}
                            </p>
                          )}
                          {text &&
                            text.content &&
                            text.content.length > 0 &&
                            text.content.map(({ nodeType, content }, index) => (
                              <RichText
                                entries={entries}
                                nodeType={nodeType}
                                content={content}
                                key={index}
                                richTextStyle={{
                                  paragraph: {
                                    customFont: cls(
                                      TEXT_BASE_CLASS['body'],
                                      {
                                        'text-dark-warm-grey':
                                          isWH &&
                                          (carouselContentStyle.hasBackground ||
                                            isDarkBackground),
                                        'text-80-white-on-dark-blue':
                                          isDD &&
                                          (carouselContentStyle.hasBackground ||
                                            isDarkBackground),
                                      },
                                      {
                                        'pl-[118px] md:pl-[111px]': isWH,
                                      }
                                    ),
                                    blockquoteFont:
                                      TEXT_BASE_CLASS['blockquote'],
                                  },
                                }}
                              />
                            ))}
                          {primaryCTA && primaryCTA.sys && (
                            <div
                              className={cls('aaco-carousel-hero__cta', {
                                'mt-5': isDD,
                                'mt-[4px] md:mt-0': isWH,
                                'pl-[118px] md:pl-[111px]':
                                  isWH && text?.content,
                              })}
                            >
                              <CtfCta
                                field={primaryCTA}
                                entries={entries}
                                invert={isDarkBackground}
                                onClick={getCtaAnalytics}
                              />
                            </div>
                          )}
                          {primaryCTA && !primaryCTA.sys && (
                            <div
                              className={cls('aaco-carousel-hero__cta', {
                                'mt-5': isDD,
                                'mt-[4px] md:mt-0': isWH,
                                'pl-[118px] md:pl-[111px]':
                                  isWH && text?.content,
                              })}
                            >
                              <Cta
                                buttonProps={{
                                  invert: isDarkBackground,
                                  name:
                                    isKorean
                                      ? '자세히 보기'
                                      : 'Learn more',
                                  type: isDD
                                    ? CtfCtaStyle.DD_SECONDARY
                                    : CtfCtaStyle.PRIMARY,
                                  style: cls('w-fit text-black', {
                                    'md:px-[15px] md:py-[8px]': isWH,
                                  }),
                                }}
                                iconProps={{
                                  name: 'arrow-right',
                                  height: 15,
                                  width: 15,
                                  style:
                                    'text-acacia dd-kr:text-actual-dd-gold dd-en:text-actual-dd-gold',
                                  hasIconBorder: true,
                                }}
                                linkProps={{
                                  url: primaryCTA,
                                }}
                                onClick={getCtaAnalytics}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </SwiperSlide>
                  );
                })}
              <div className="swiper-control">
                <div
                  className={cls(
                    'aaco-carousel-featured__pagination',
                    'flex items-center',
                    {
                      'mt-10 md:mt-[60px]': isDD,
                      'mt-2 md:mt-6': isWH,
                    }
                  )}
                  ref={paginationRef}
                ></div>
              </div>
            </Swiper>
          </div>
        </div>
      </div>
    </div>
  );
}

CarouselFeatured.propTypes = {
  assets: PropTypes.array,
  carouselContentStyle: PropTypes.object,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

export default CarouselFeatured;
